/**
 * The favorites duck. Default export is the reducer
 *
 * Action Types:
 * - FAVORITES_REQUESTED
 * - FAVORITES_SUCCEEDED
 * - FAVORITES_REQUEST_FAILED
 * - ADD_FAVORITE_REQUEST
 * - ADD_FAVORITE_SUCCESS
 * - ADD_FAVORITE_FAILED
 * - REMOVE_FAVORITE_REQUEST
 * - REMOVE_FAVORITE_SUCCESS
 * - REMOVE_FAVORITE_FAILED
 *
 * Action Creators:
 * - requestFavorites
 * - favoritesSucceeded
 * - favoritesRequestFailed
 * - addFavorite
 * - addFavoriteSucceeded
 * - addFavoriteFailed
 * - removeFavorite
 * - removeFavoriteSucceeded
 * - removeFavoriteFailed
 */
import get from 'lodash/get';
import without from 'lodash/without';
import { LOG_OUT_REQUESTED } from './account';
import type {
  FavoritesAction,
  FavoritesRequested,
  FavoritesSucceeded,
  FavoritesRequestFailed,
  AddFavoriteRequest,
  AddFavoriteSuccess,
  AddFavoriteFailed,
  RemoveFavoriteRequest,
  RemoveFavoriteSuccess,
  RemoveFavoriteFailed,
  FavoritesState,
} from '../types';

/** @type {ActionType} */
export const FAVORITES_REQUESTED = 'PB/favorites/FAVORITES_REQUESTED';

/** @type {ActionType} */
export const FAVORITES_SUCCEEDED = 'PB/favorites/FAVORITES_SUCCEEDED';

/** @type {ActionType} */
export const FAVORITES_REQUEST_FAILED = 'PB/favorites/FAVORITES_REQUEST_FAILED';

/** @type {ActionType} */
export const ADD_FAVORITE_REQUEST = 'PB/favorites/ADD_FAVORITE_REQUEST';

/** @type {ActionType} */
export const ADD_FAVORITE_SUCCESS = 'PB/favorites/ADD_FAVORITE_SUCCESS';

/** @type {ActionType} */
export const ADD_FAVORITE_FAILED = 'PB/favorites/ADD_FAVORITE_FAILED';

/** @type {ActionType} */
export const REMOVE_FAVORITE_REQUEST = 'PB/favorites/REMOVE_FAVORITE_REQUEST';

/** @type {ActionType} */
export const REMOVE_FAVORITE_SUCCESS = 'PB/favorites/REMOVE_FAVORITE_SUCCESS';

/** @type {ActionType} */
export const REMOVE_FAVORITE_FAILED = 'PB/favorites/REMOVE_FAVORITE_FAILED';

export const initialState = {
  favorites: [],
};

/** @ignore */
export default function favorites(
  state: FavoritesState = initialState,
  action: FavoritesAction,
): FavoritesState {
  switch (action.type) {
    case FAVORITES_SUCCEEDED:
      const favorites = get(action, 'payload.favorites') || [];
      return { ...state, favorites };

    case ADD_FAVORITE_SUCCESS:
      const addedIds = state.favorites.concat(get(action, 'payload.packageId'));
      return { ...state, favorites: addedIds };

    case REMOVE_FAVORITE_SUCCESS:
      const prunedIds = without(
        state.favorites,
        get(action, 'payload.packageId'),
      );
      return { ...state, favorites: prunedIds };

    case LOG_OUT_REQUESTED:
      return { ...state, favorites: [] };

    default:
      return state;
  }
}

/**
 * This action signifies that we have requested the user's favorites.
 *
 * @return {Action}
 * @property {string} type FAVORITES_REQUESTED
 */
export function requestFavorites(): FavoritesRequested {
  return {
    type: FAVORITES_REQUESTED,
  };
}

/**
 * This action signifies that we have properly retrieved a user's favorites.
 * In the form of an object with a list of package ids.
 *
 * @param {Object} favorites - a list of the user's arrays.
 * @return {Action}
 * @property {string} type FAVORITES_SUCCEEDED
 * @property {Object} payload
 */
export function favoritesSucceeded(favorites: number[]): FavoritesSucceeded {
  return {
    type: FAVORITES_SUCCEEDED,
    payload: { favorites },
  };
}

/**
 * This action signifies that were unable to fetch a users favorites.
 * Most likely meaning that the API is down or the user is not logged in.
 * This will change once we implement favoriting while logged out.
 * The implementation of that may depend on local storage rather than an API though.
 *
 * @return {Action}
 * @property {string} type FAVORITES_REQUEST_FAILED
 * @property {Object} payload
 */
export function favoritesRequestFailed(
  message: string,
): FavoritesRequestFailed {
  return {
    type: FAVORITES_REQUEST_FAILED,
    payload: { message },
  };
}

/**
 * This action signifies that a user has requested to favorite a package.
 *
 * @return {Action}
 * @property {string} type ADD_FAVORITE_REQUEST
 */
export function addFavorite(packageId: number): AddFavoriteRequest {
  return {
    type: ADD_FAVORITE_REQUEST,
    payload: { packageId },
  };
}

/**
 * This action signifies that we have properly
 * added a package to a users favorites.
 * In the form of an integer with a package id.
 *
 * @param {Object} packageId - the id of the package the user successfully added.
 * @return {Action}
 * @property {string} type ADD_FAVORITE_SUCCESS
 * @property {Object} payload
 */
export function addFavoriteSucceeded(packageId: number): AddFavoriteSuccess {
  return {
    type: ADD_FAVORITE_SUCCESS,
    payload: { packageId },
  };
}

/**
 * This action signifies that we were unable to
 * add a package to a users favorites.
 * In the form of a number representing a package id.
 *
 * @param {Object} packageId - the id of the package the user was unable to add.
 * @return {Action}
 * @property {string} type ADD_FAVORITE_FAILED
 * @property {Object} payload
 */
export function addFavoriteFailed(message: string): AddFavoriteFailed {
  return {
    type: ADD_FAVORITE_FAILED,
    payload: { message },
  };
}

/**
 * This action signifies that a user has requested to unfavorite a package.
 *
 * @return {Action}
 * @property {string} type REMOVE_FAVORITE_REQUEST
 */
export function removeFavorite(packageId: number): RemoveFavoriteRequest {
  return {
    type: REMOVE_FAVORITE_REQUEST,
    payload: { packageId },
  };
}

/**
 * This action signifies that we have properly
 * removed a package to a users favorites.
 * In the form of an integer with a package id.
 *
 * @param {Object} packageId - the id of the package the user successfully removed.
 * @return {Action}
 * @property {string} type REMOVE_FAVORITE_SUCCESS
 * @property {Object} payload
 */
export function removeFavoriteSucceeded(
  packageId: number,
): RemoveFavoriteSuccess {
  return {
    type: REMOVE_FAVORITE_SUCCESS,
    payload: { packageId },
  };
}

/**
 * This action signifies that we were unable to
 * remove a package to a users favorites.
 * In the form of a number representing a package id.
 *
 * @param {Object} packageId - the id of the package the user was unable to remove.
 * @return {Action}
 * @property {string} type REMOVE_FAVORITE_FAILED
 * @property {Object} payload
 */
export function removeFavoriteFailed(message: string): RemoveFavoriteFailed {
  return {
    type: REMOVE_FAVORITE_FAILED,
    payload: { message },
  };
}
